import {getDamageDiceFaceOutcomes} from "../../dice/getDamageDiceFaceOutcomes";
import {checkIfBotHasDiceBonus, checkIfBotShouldRerollDamageDie} from "./checkIfBotShouldRerollDamageDie";

const NO_REROLL = {isDice1Selected: false, isDice2Selected: false};

// Варианты переброса по порядку предпочтения при равном выигрыше: меньше
// костей — меньше разброс
const REROLL_OPTIONS = [
  {isDice1Selected: true, isDice2Selected: false},
  {isDice1Selected: false, isDice2Selected: true},
  {isDice1Selected: true, isDice2Selected: true},
];

// Выигрыш меньше этого — погрешность деления, а не выигрыш
const MIN_EXPECTED_GAIN = 1e-9;

/**
 * Какие кости урона перебросить боту за дополнительное снабжение — в стрельбе
 * и в ближнем бою.
 *
 * Бот без подкруток костей и без штрафа форы новичкам решает по каждой
 * кости, как раньше (-2 и хуже, см. checkIfBotShouldRerollDamageDie). С
 * подкрутками или со штрафом одной кости мало: урон
 * катапульты берет худшую из двух костей, если отряд ходил, и лучшую, если
 * стоял; эльфийская стрельба делит сумму пополам; урон сверх нужного для
 * уничтожения цели пропадает; орки и отряды в крепости теряют численность
 * ступенями. Кость "хуже среднего" тут бывает бесполезно или даже вредно
 * перебрасывать (у ходившей катапульты 0 и 0 при поле 3 и +1: худшая из двух
 * новых костей в среднем ниже нуля).
 *
 * Поэтому перебираются варианты "первая кость", "вторая", "обе", и для
 * каждого считается средняя польза по всем равновероятным исходам переброса
 * при этих подкрутках. Перебрасывается вариант с наибольшим выигрышем, если он
 * строго больше нуля.
 *
 * Со штрафом форы (facePenalty) так решает и бот без подкруток: лучшая грань
 * +1 ему недоступна, и правило «-2 и хуже» у катапульты перестает быть
 * выгодным — у стоявшей при костях 0 и -3 переброс урон не поднимет никогда,
 * у ходившей при -2 и -2 в среднем его опустит, а дополнительное снабжение
 * сгорит. По самой кости такой бот решает, только когда пользу исхода не
 * дали (getValue нет).
 *
 * @param {Object} params
 * @param {number} params.diceRoll1 - текущая грань первой кости
 * @param {number} params.diceRoll2 - текущая грань второй кости
 * @param {number} [params.minRoll=1] - пол броска d6 при перебросе
 * @param {number} [params.rollBonus=0] - прибавка к значению d6 при перебросе
 * @param {number} [params.facePenalty=0] - штраф к грани при перебросе (фора
 *   новичкам, см. getLowRatingAdvantageDicePenalty)
 * @param {function(number, number): number} params.getValue - польза исхода с
 *   такими гранями (см. createBotShotValueGetter, createBotMeleeValueGetter)
 * @returns {{isDice1Selected: boolean, isDice2Selected: boolean}}
 */
export const selectBotRerollDice = ({
  diceRoll1, diceRoll2, minRoll = 1, rollBonus = 0, facePenalty = 0, getValue,
}) => {
  const isDecidedByOutcomeValue = checkIfBotHasDiceBonus(minRoll, rollBonus)
    || (facePenalty > 0 && Boolean(getValue));
  if (!isDecidedByOutcomeValue) {
    return {
      isDice1Selected: checkIfBotShouldRerollDamageDie(diceRoll1, minRoll, rollBonus, facePenalty),
      isDice2Selected: checkIfBotShouldRerollDamageDie(diceRoll2, minRoll, rollBonus, facePenalty),
    };
  }

  const outcomes = getDamageDiceFaceOutcomes(minRoll, rollBonus, facePenalty);
  // Лучшая грань, которая вообще может выпасть при перебросе: +1, а со
  // штрафом форы новичкам — 0
  const bestFace = Math.max(...outcomes);
  const currentValue = getValue(diceRoll1, diceRoll2);

  let bestOption = NO_REROLL;
  let bestGain = 0;
  REROLL_OPTIONS.forEach((option) => {
    // Лучшую грань перебрасывать незачем — больше она не станет
    if ((option.isDice1Selected && diceRoll1 >= bestFace)
      || (option.isDice2Selected && diceRoll2 >= bestFace)) {
      return;
    }

    const faces1 = option.isDice1Selected ? outcomes : [diceRoll1];
    const faces2 = option.isDice2Selected ? outcomes : [diceRoll2];
    let valueSum = 0;
    faces1.forEach((face1) => {
      faces2.forEach((face2) => {
        valueSum += getValue(face1, face2);
      });
    });
    const gain = valueSum / (faces1.length * faces2.length) - currentValue;

    if (gain > bestGain + MIN_EXPECTED_GAIN) {
      bestOption = option;
      bestGain = gain;
    }
  });

  return bestOption;
};
